import {
  useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { Send, X, Loader2, AlertTriangle, BookOpen, PackageSearch } from 'lucide-react';
import type { TurnstileInstance } from '@marsidev/react-turnstile';
import { EvieOrb } from './EvieOrb';
import { ShopTurnstile, hasTurnstile } from './ShopTurnstile';
import { getEvieSessionId } from '@/lib/evieSession';
import { postEvieMessage, readEvieError, type EvieToolCall } from '@/lib/api';
import { PARTNERS_APPLY_URL } from '@/lib/partnerLinks';
import styles from './EvieSheet.module.css';

/**
 * Public shop EVII: the anonymous assistant sheet on evinitiative.shop. A lean,
 * text-only sibling of the driver app's DriverAssistSheet: same orb + glass
 * chrome, none of the app machinery (no voice, no history, no receipts, no
 * in-app navigation actions, no account). It talks to POST
 * /ai-assistant/public/message with a client-minted session id.
 *
 * Turnstile FAIL-CLOSED: a token is required to OPEN the session (one human
 * check), after which the session flows freely. The composer stays disabled
 * until the widget yields a live token; if the server later reports the session
 * lost (TURNSTILE_REQUIRED/FAILED) we drop back to the verify gate.
 *
 * Conversation state is local and survives close/reopen because the sheet is
 * mounted once at ShopLayout (which does not unmount across route changes). It
 * intentionally does NOT persist across reloads; a fresh load starts clean.
 */

interface Message {
  kind: 'user' | 'assistant' | 'error';
  text: string;
  tools?: EvieToolCall[];
}

const GREETING =
  "Hi, I'm EVII™. I can help you pick the right charger, understand how the "
  + 'EV Initiative platform works, and answer setup questions. What are you '
  + 'shopping for?';

const QUICK_ACTIONS = [
  { label: 'Which charger fits my home?', prompt: 'Which charger is right for my home?' },
  { label: 'What do you sell for commercial sites?', prompt: 'What do you offer for commercial charging sites?' },
] as const;

const EMPTY_REPLY_FALLBACK =
  'Sorry, I had trouble responding. Please try again, or reach us at hello@evinitiative.com.';

// Internal shop routes EVII may cite in a reply ("see it at /home-charging").
// We linkify only these exact paths so the "page link" lands as a real click.
const KNOWN_ROUTES = new Set([
  '/commercial-dc', '/commercial-ac', '/home-charging', '/accessories', '/cart',
]);
// The Sales Partner program lives on evinitiative.com; EVII names it as
// evinitiative.com/partners, which becomes a plain link to the application.
const PARTNERS_TEXT = /^(?:https:\/\/)?evinitiative\.com\/partners(?:#start)?$/;
const ROUTE_SPLIT =
  /((?:https:\/\/)?evinitiative\.com\/partners(?:#start)?|\/(?:commercial-dc|commercial-ac|home-charging|accessories|cart))/;

/** Render assistant text, turning known shop paths into router links (which
 *  close the sheet on tap so the visitor lands on the page) and
 *  evinitiative.com/partners into a same-tab link. Everything else is plain
 *  text with preserved line breaks (via CSS white-space). */
function renderAssistantText(text: string, onNavigate: () => void): ReactNode[] {
  return text.split(ROUTE_SPLIT).map((part, i) => {
    if (KNOWN_ROUTES.has(part)) {
      return (
        <Link key={i} to={part} className={styles.inlineLink} onClick={onNavigate}>
          {part}
        </Link>
      );
    }
    if (PARTNERS_TEXT.test(part)) {
      return <a key={i} href={PARTNERS_APPLY_URL} className={styles.inlineLink}>{part}</a>;
    }
    return <span key={i}>{part}</span>;
  });
}

/** Friendly label for an inline tool-status pill. */
function toolLabel(name: string): { label: string; icon: ReactNode } {
  if (name === 'get_public_catalog') return { label: 'Checked the catalog', icon: <PackageSearch size={12} /> };
  if (name === 'manual_lookup') return { label: 'Checked the manual', icon: <BookOpen size={12} /> };
  return { label: name, icon: <PackageSearch size={12} /> };
}

export function EvieSheet({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const sessionId = useMemo(() => getEvieSessionId(), []);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  // Whether the server-side session is open. Once true, no more Turnstile.
  const [opened, setOpened] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const tsRef = useRef<TurnstileInstance>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Seed the greeting once, on first open.
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ kind: 'assistant', text: GREETING }]);
    }
  }, [isOpen, messages.length]);

  // Escape to close + lock body scroll while the sheet is open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.documentElement.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  // Auto-scroll to newest + focus the composer when ready.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);
  useEffect(() => {
    if (isOpen && (opened || token)) inputRef.current?.focus();
  }, [isOpen, opened, token]);

  const handleSend = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      // Fail-closed: the first message needs a live Turnstile token to open the
      // session. After that the session flows without one.
      if (!trimmed || sending || (!opened && !token)) return;
      setMessages((prev) => [...prev, { kind: 'user', text: trimmed }]);
      setInput('');
      setSending(true);
      try {
        const reply = await postEvieMessage({
          session_id: sessionId,
          message: trimmed,
          ...(opened ? {} : token ? { turnstile_token: token } : {}),
        });
        setOpened(true);
        setMessages((prev) => [
          ...prev,
          {
            kind: 'assistant',
            text: reply.assistant_message || EMPTY_REPLY_FALLBACK,
            tools: reply.tool_calls_made,
          },
        ]);
      } catch (err) {
        const body = await readEvieError(err);
        const code = body?.code;
        if (code === 'TURNSTILE_REQUIRED' || code === 'TURNSTILE_FAILED') {
          // Session was never opened / has been lost: drop back to the gate.
          setOpened(false);
          setToken(null);
          tsRef.current?.reset();
          setMessages((prev) => [
            ...prev,
            { kind: 'error', text: 'Please complete the quick verification below to start the chat.' },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              kind: 'error',
              text: body?.message ?? 'Something went wrong. Please try again, or reach us at hello@evinitiative.com.',
            },
          ]);
        }
      } finally {
        setSending(false);
      }
    },
    [opened, sending, sessionId, token],
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void handleSend(input);
  };

  if (!isOpen) return null;

  const showGate = !opened;
  const showQuickActions = messages.length <= 1 && !sending && (opened || !!token);
  const canSend = !sending && !!input.trim() && (opened || !!token);

  return (
    <div className={styles.backdrop} onClick={onClose} role="presentation">
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label="EVII™ assistant"
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.header}>
          <div className={styles.headerTitle}>
            <EvieOrb size={22} />
            <span>EVII&trade;</span>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div ref={scrollRef} className={styles.transcript} aria-live="polite">
          {messages.map((m, i) => (
            <MessageRow key={i} m={m} onNavigate={onClose} />
          ))}
          {sending && (
            <div className={styles.typing}>
              <Loader2 size={14} className={styles.spin} />
              <span>Thinking…</span>
            </div>
          )}
        </div>

        {showQuickActions && (
          <div className={styles.quickActions}>
            {QUICK_ACTIONS.map((qa) => (
              <button
                key={qa.label}
                type="button"
                className={styles.quickAction}
                onClick={() => void handleSend(qa.prompt)}
              >
                {qa.label}
              </button>
            ))}
          </div>
        )}

        {showGate && (
          <div className={styles.gate}>
            {hasTurnstile ? (
              <>
                <p className={styles.gateHint}>One quick check to start the chat.</p>
                <ShopTurnstile ref={tsRef} onToken={setToken} />
              </>
            ) : (
              <p className={styles.gateErr}>
                Verification is unavailable right now. Please reach us at hello@evinitiative.com.
              </p>
            )}
          </div>
        )}

        <form onSubmit={onSubmit} className={styles.composer}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={opened || token ? 'Ask EVII™ about our chargers…' : 'Verify above to start…'}
            maxLength={2000}
            disabled={sending || (!opened && !token)}
            className={styles.composerInput}
            aria-label="Message"
          />
          <button type="submit" disabled={!canSend} className={styles.composerSend} aria-label="Send">
            <Send size={16} />
          </button>
        </form>
        <p className={styles.scopeHint}>
          EVII&trade; helps with EV Initiative chargers, the platform, and setup. She can&rsquo;t place
          orders or access accounts.
        </p>
      </div>
    </div>
  );
}

function MessageRow({ m, onNavigate }: { m: Message; onNavigate: () => void }) {
  if (m.kind === 'user') {
    return (
      <div className={`${styles.bubble} ${styles.bubbleUser}`}>
        <p className={styles.bubbleText}>{m.text}</p>
      </div>
    );
  }
  if (m.kind === 'error') {
    return (
      <div className={`${styles.bubble} ${styles.bubbleError}`}>
        <div className={styles.bubbleHead}>
          <AlertTriangle size={13} />
          <span>Try again</span>
        </div>
        <p className={styles.bubbleText}>{m.text}</p>
      </div>
    );
  }
  return (
    <div className={`${styles.bubble} ${styles.bubbleAssistant}`}>
      <div className={styles.bubbleHead}>
        <EvieOrb size={14} />
        <span>EVII&trade;</span>
      </div>
      {m.tools && m.tools.length > 0 && (
        <div className={styles.toolList}>
          {m.tools.map((tc, i) => {
            const { label, icon } = toolLabel(tc.name);
            return (
              <span key={i} className={styles.toolPill} title={tc.result_summary}>
                {icon}
                {label}
              </span>
            );
          })}
        </div>
      )}
      <p className={styles.bubbleText}>{renderAssistantText(m.text, onNavigate)}</p>
    </div>
  );
}
